/**
 * ProductCompatibilityMatrix.tsx
 * 
 * Visual matrix showing which Plaid products are supported by the institution
 */

import { FC } from 'react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';

interface ProductCompatibilityMatrixProps {
  supportedProducts: string[];
  requiredProducts?: string[];
  allProducts?: string[];
}

const ProductCompatibilityMatrix: FC<ProductCompatibilityMatrixProps> = ({
  supportedProducts,
  requiredProducts = ['transactions', 'auth', 'identity', 'balance'],
  allProducts = [
    'transactions', 
    'auth', 
    'identity', 
    'balance', 
    'income', 
    'investments', 
    'liabilities',
    'assets',
    'credit_details',
    'identity_match',
    'income_verification',
    'processor_payments',
    'transfer',
    'signal'
  ]
}) => {
  const isSupported = (product: string) => supportedProducts.includes(product);
  const isRequired = (product: string) => requiredProducts.includes(product);

  const getStatusColor = (product: string) => {
    if (!isSupported(product) && isRequired(product)) {
      return 'text-red-600'; // Required but not supported
    }
    if (isSupported(product) && isRequired(product)) {
      return 'text-green-600'; // Required and supported
    }
    if (isSupported(product)) {
      return 'text-blue-600'; // Supported but not required
    }
    return 'text-slate-400'; // Not supported and not required
  };

  const getIcon = (product: string) => {
    if (isSupported(product)) {
      return <FiCheckCircle size={16} className={getStatusColor(product)} />;
    }
    return <FiXCircle size={16} className="text-slate-300" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-slate-700 mb-4">
        Product Compatibility Matrix
      </h3>

      <div className="mb-4 flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <FiCheckCircle size={14} className="text-green-600" />
          <span className="text-slate-600">Required & Supported</span>
        </div>
        <div className="flex items-center gap-1.5">
          <FiCheckCircle size={14} className="text-blue-600" />
          <span className="text-slate-600">Optional & Supported</span>
        </div>
        <div className="flex items-center gap-1.5">
          <FiXCircle size={14} className="text-red-600" />
          <span className="text-slate-600">Required & Missing</span>
        </div>
        <div className="flex items-center gap-1.5">
          <FiXCircle size={14} className="text-slate-300" />
          <span className="text-slate-600">Not Supported</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {allProducts.map((product) => (
          <div
            key={product}
            className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
              !isSupported(product) && isRequired(product)
                ? 'bg-red-50 border-red-200'
                : isSupported(product) && isRequired(product)
                ? 'bg-green-50 border-green-200'
                : isSupported(product)
                ? 'bg-blue-50 border-blue-200'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <div className="flex items-center gap-2 flex-1">
              {getIcon(product)}
              <span className={`text-sm font-medium ${
                isSupported(product) ? 'text-slate-800' : 'text-slate-500'
              }`}>
                {product.replace(/_/g, ' ')}
              </span>
            </div>
            {isRequired(product) && (
              <span className="text-xs font-semibold text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
                Required
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600">
        <div className="flex items-center justify-between">
          <span>Total Supported Products:</span>
          <span className="font-semibold text-slate-800">{supportedProducts.length}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span>Required Products Missing:</span>
          <span className={`font-semibold ${
            requiredProducts.filter(p => !supportedProducts.includes(p)).length === 0
              ? 'text-green-700'
              : 'text-red-700'
          }`}>
            {requiredProducts.filter(p => !supportedProducts.includes(p)).length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProductCompatibilityMatrix;
